import React, { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { ThemeContext } from 'styled-components';

import {
  getDateTimeObjFromDateTimeStr,
  getDateDetailsFromDateTimeObj,
  getTimeDetailsFromDateTimeObj,
} from 'utils/functions/dateTime';
import { staticDataInterestsMapper } from 'utils/functions/objectMapper';
import { trimText } from 'utils/functions/general';
import postsStrings from 'modules/posts/strings';
import Popup from 'modules/common/components/Popup';
import Loader from 'modules/common/components/Loader';
import SectionDivider from 'modules/common/components/SectionDivider';

import {
  Wrapper,
  GridWrapper,
  Header,
  Divider,
  ItemColumn,
  ItemColumnWithHover,
  ItemRow,
  ItemText,
  ItemClick,
  ItemHoverWrapper,
  ItemHover,
  ItemTag,
  ItemBtn,
  /* ####################### */
  ActionsPopupWrapper,
  ActionsPopupTitleWrapper,
  ActionsPopupTitle,
  ActionsPopupBtnWrapper,
  ActionsPopupYesBtn,
  ActionsPopupNoBtn,
} from './PostsList.style';

const PostActionsPopup = ({
  themeContext,
  isActionPopupLoading,
  handleActionPopupConfirmBtnClick,
  handleActionPopupDismissBtnClick,
}) => (
  <Popup handleCloseBtnClick={() => handleActionPopupDismissBtnClick()}>
    <ActionsPopupWrapper>
      <ActionsPopupTitleWrapper>
        <ActionsPopupTitle>{postsStrings.postsList.deleteActionPopupTitle}</ActionsPopupTitle>
      </ActionsPopupTitleWrapper>
      <SectionDivider
        gap={16}
        withSeparator
        separatorGap={16}
        borderColor={themeContext.colors.grey004}
      />
      <ActionsPopupBtnWrapper>
        {(isActionPopupLoading && <Loader width={28} height={28} thickness={4} />) || (
          <>
            <ActionsPopupYesBtn as="button" onClick={() => handleActionPopupConfirmBtnClick()}>
              {postsStrings.postsList.deleteActionPopupYesBtn}
            </ActionsPopupYesBtn>
            <ActionsPopupNoBtn as="button" onClick={() => handleActionPopupDismissBtnClick()}>
              {postsStrings.postsList.deleteActionPopupNoBtn}
            </ActionsPopupNoBtn>
          </>
        )}
      </ActionsPopupBtnWrapper>
    </ActionsPopupWrapper>
  </Popup>
);
PostActionsPopup.propTypes = {
  themeContext: PropTypes.shape({
    colors: PropTypes.shape({
      grey004: PropTypes.string,
    }),
  }).isRequired,
  isActionPopupLoading: PropTypes.bool.isRequired,
  handleActionPopupConfirmBtnClick: PropTypes.func.isRequired,
  handleActionPopupDismissBtnClick: PropTypes.func.isRequired,
};

/* ############################################################################### */
/* ############################################################################### */
/* ############################################################################### */

const PostsList = ({
  postsListArr,
  isActionPopupOpen,
  isActionPopupLoading,
  handleTextToCopyToClipboardClick,
  handlePreviewFileClick,
  handleActionBtnClick,
  handleActionPopupConfirmBtnClick,
  handleActionPopupDismissBtnClick,
}) => {
  const { staticData } = useSelector(state => ({
    staticData: state.common.staticData,
  }));
  const { details: staticDetails } = staticData;

  const mappedInterests = useMemo(
    () => staticDataInterestsMapper(staticDetails.interests),
    [staticDetails],
  );

  const themeContext = useContext(ThemeContext);
  const { colors } = themeContext;

  const postsGridHeaderList = postsStrings.postsList.postsGridHeader;

  return (
    <>
      <Wrapper>
        <GridWrapper>
          <Divider />
          {Object.keys(postsGridHeaderList).map(postsGridHeaderKey => (
            <Header key={postsGridHeaderKey}>{postsGridHeaderList[postsGridHeaderKey]}</Header>
          ))}
          <Divider />
          {postsListArr.map((post, index) => {
            const {
              postKey,
              content,
              createdDate,
              interests,
              attachments,
              reactionsCount,
              commentsCount,
              // repostsCount,
            } = post;

            const postDateTimeObj = getDateTimeObjFromDateTimeStr(createdDate, true);
            const { dateObj } = postDateTimeObj;
            const localDateObj = new Date(dateObj.setHours(dateObj.getHours() + 3));
            const { day, month, year } = getDateDetailsFromDateTimeObj(localDateObj);
            const { hours, minutes, period } = getTimeDetailsFromDateTimeObj(localDateObj);
            const postDateTimeStr = `${day} ${month} ${year} ${hours}:${minutes} ${period}`;

            const isLastItem = index === postsListArr.length - 1;

            return (
              <React.Fragment key={postKey}>
                <ItemColumn>
                  <ItemClick onClick={() => handleTextToCopyToClipboardClick(postKey)}>
                    {trimText(postKey, 8)}
                  </ItemClick>
                </ItemColumn>
                <ItemColumnWithHover>
                  <ItemText>{trimText(content, 300)}</ItemText>
                  {content && (
                    <ItemHoverWrapper>
                      <ItemHover>{content}</ItemHover>
                    </ItemHoverWrapper>
                  )}
                </ItemColumnWithHover>
                <ItemRow padding>
                  {attachments.map(media => {
                    const { attachmentType, url } = media;
                    const mappedAttachmentType = attachmentType.toLowerCase();

                    return (
                      <ItemTag
                        key={url}
                        margin
                        hover
                        onClick={() => handlePreviewFileClick(true, mappedAttachmentType, url)}
                      >
                        {attachmentType}
                      </ItemTag>
                    );
                  })}
                </ItemRow>
                <ItemRow padding>
                  {interests.map(interest => (
                    <ItemTag key={interest} margin>
                      {mappedInterests[interest]}
                    </ItemTag>
                  ))}
                </ItemRow>
                <ItemColumn>
                  <ItemText>{postDateTimeStr}</ItemText>
                </ItemColumn>
                <ItemColumn>
                  <ItemText>{reactionsCount || 0}</ItemText>
                </ItemColumn>
                <ItemColumn>
                  <ItemText>{commentsCount || 0}</ItemText>
                </ItemColumn>
                <ItemRow>
                  <ItemBtn
                    as="button"
                    icon={'"\\e91f"'}
                    color={colors.redMain}
                    backgroundColor={colors.redLight}
                    onClick={() => handleActionBtnClick(postKey)}
                  />
                </ItemRow>
                {isLastItem === false && <Divider />}
              </React.Fragment>
            );
          })}
        </GridWrapper>
      </Wrapper>
      {isActionPopupOpen && (
        <PostActionsPopup
          themeContext={themeContext}
          isActionPopupLoading={isActionPopupLoading}
          handleActionPopupConfirmBtnClick={handleActionPopupConfirmBtnClick}
          handleActionPopupDismissBtnClick={handleActionPopupDismissBtnClick}
        />
      )}
    </>
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

PostsList.propTypes = {
  postsListArr: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isActionPopupOpen: PropTypes.bool.isRequired,
  isActionPopupLoading: PropTypes.bool.isRequired,
  handleTextToCopyToClipboardClick: PropTypes.func.isRequired,
  handlePreviewFileClick: PropTypes.func.isRequired,
  handleActionBtnClick: PropTypes.func.isRequired,
  handleActionPopupConfirmBtnClick: PropTypes.func.isRequired,
  handleActionPopupDismissBtnClick: PropTypes.func.isRequired,
};

PostsList.defaultProps = {};

export default PostsList;
